import React from 'react';
import { CalendarCheck } from 'lucide-react';
import { supabase } from '../../supabaseClient';
import { useSession } from '../../lib/SessionContext';
import { t } from '../../i18n';
import { Field } from '../../components/ui';
import { inRange, isBlank } from '../../lib/settingsForm';
import SettingsCard, { useSettingsCard, NumberWithUnit } from './SettingsCard';

/**
 * The emails around a session: the calendar invitation the professional gets,
 * and how long before a session their patients are reminded (0060). An empty
 * reminder time is the practice's, which admin sets in Consultorio.
 */
const NoticesTab = () => {
  const { profile, settings, refresh } = useSession();
  const practiceHours = settings?.reminder_hours_before ?? 24;
  const invites = useSettingsCard('calendar-invites', {
    wants_calendar_invites: profile.wants_calendar_invites ?? true,
  });
  const reminder = useSettingsCard('patient-reminder', {
    reminder_hours_before: profile.reminder_hours_before ?? '',
  });

  const saveInvites = () => invites.save(async (typed) => {
    const { error } = await supabase.from('therapists')
      .update({ wants_calendar_invites: typed.wants_calendar_invites })
      .eq('id', profile.id);
    if (error) return error.message;
    refresh();
    return null;
  });

  // The database refuses anything outside 1–168 too.
  const saveReminder = () => reminder.save(async (typed) => {
    const empty = isBlank(typed.reminder_hours_before);
    if (!empty && !inRange(typed.reminder_hours_before, [1, 168])) return t('mySettings.reminderInvalid');
    const { error } = await supabase.from('therapists')
      .update({ reminder_hours_before: empty ? null : Number(typed.reminder_hours_before) })
      .eq('id', profile.id);
    if (error) return error.message;
    refresh();
    return empty ? { saved: { reminder_hours_before: '' } } : null;
  });

  return (
    <div className="space-y-4">
      <SettingsCard title={t('mySettings.invitesTitle')} card={invites.card} onSave={saveInvites}>
        <label className="flex items-start gap-3">
          <input
            type="checkbox"
            className="mt-1"
            checked={invites.form.wants_calendar_invites}
            onChange={(event) => invites.change({ wants_calendar_invites: event.target.checked })}
          />
          <span>
            <span className="flex items-center gap-1.5 font-medium text-slate-800">
              <CalendarCheck size={15} />{t('profile.calendarInvites')}
            </span>
            <span className="mt-0.5 block text-sm text-slate-500">{t('profile.calendarInvitesHint')}</span>
          </span>
        </label>
      </SettingsCard>

      <SettingsCard
        title={t('mySettings.reminderTitle')}
        subtitle={t('mySettings.reminderHint', { hours: practiceHours })}
        card={reminder.card}
        onSave={saveReminder}
      >
        <Field label={t('mySettings.reminderHours')}>
          <NumberWithUnit
            min="1" max="168" placeholder={String(practiceHours)} unit={t('settings.hoursBefore')}
            value={reminder.form.reminder_hours_before}
            onChange={(event) => reminder.change({ reminder_hours_before: event.target.value })}
          />
        </Field>
      </SettingsCard>
    </div>
  );
};

export default NoticesTab;
